import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useMyGroupTickets } from '@/hooks/useMyGroupTickets';
import { getUrgenciaColor, getStatusColor, getSLAStatus } from '@/utils/slaStatus';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Users, Loader2 } from 'lucide-react';

interface MyGroupTicketsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketClick: (ticket: any) => void;
}

const MyGroupTicketsModal = ({ isOpen, onClose, onTicketClick }: MyGroupTicketsModalProps) => {
  const { data: tickets = [], isLoading } = useMyGroupTickets(isOpen);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5" />
            Meus Tickets de Grupo ({tickets.length})
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Carregando tickets...</p>
              </div>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Nenhum ticket encontrado</p>
              <p className="text-sm">Não há tickets abertos onde seus grupos são responsáveis</p>
            </div>
          ) : (
            <>
              <div className="grid gap-3">
                {tickets.map((ticket) => {
                  const slaStatus = getSLAStatus(ticket);
                  return (
                    <Card key={ticket.id} className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-blue-500">
                      <CardContent className="p-4" onClick={() => onTicketClick(ticket)}>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-blue-600">{ticket.numero}</span>
                            <Badge className={`${getUrgenciaColor(ticket.prioridade)} capitalize`}>
                              {ticket.prioridade}
                            </Badge>
                            <Badge className={slaStatus.color}>
                              {slaStatus.label}
                            </Badge>
                          </div>
                          <Badge variant="outline" className={getStatusColor(ticket.status)}>
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1">
                          {ticket.titulo}
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-2">
                          <div>
                            <span className="font-medium">Solicitante:</span> {ticket.solicitante?.name || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Cliente:</span> {ticket.cliente?.name || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Grupo:</span> {ticket.grupo_responsavel?.name || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Atendente:</span> {ticket.atendente?.name || 'Não atribuído'}
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>
                            Criado em: {format(new Date(ticket.criado_em), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                          </span>
                          {ticket.data_limite_resolucao && (
                            <span>
                              Prazo: {format(new Date(ticket.data_limite_resolucao), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t">
                <span className="text-sm text-gray-600">
                  {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} encontrado{tickets.length !== 1 ? 's' : ''}
                </span>
                <Button variant="outline" onClick={onClose}>
                  Fechar
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MyGroupTicketsModal;